import * as React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { loginSchema } from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
} from '~/components/ui/primitives';
import { homeRouteFor, useAuth } from '~/lib/auth';
import { messageOf } from '~/lib/queries';

export function LoginPage() {
  const { login, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);

  // Un utilisateur deja connecte n'a rien a faire sur cette page.
  if (!isLoading && user) {
    return <Navigate to={homeRouteFor(user.role)} replace />;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});

    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [issue.path.join('.'), issue.message]),
        ),
      );
      return;
    }

    setSubmitting(true);
    try {
      const account = await login(parsed.data);
      toast.success(`Bienvenue, ${account.firstName}.`);
      // Retour a la page initialement demandee, sinon accueil du role.
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? homeRouteFor(account.role), { replace: true });
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col justify-center px-4 py-12">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Connexion</CardTitle>
          <CardDescription>Accedez a vos rendez-vous et a vos cycles.</CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            <Field label="Adresse e-mail" htmlFor="email" error={errors.email} required>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                placeholder="vous@exemple.fr"
              />
            </Field>

            <Field label="Mot de passe" htmlFor="password" error={errors.password} required>
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </Field>

            <Button type="submit" loading={submitting} className="w-full">
              Se connecter
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            Pas encore de compte ?{' '}
            <Link
              to="/reserver"
              className="font-medium text-foreground underline underline-offset-4"
            >
              Prenez rendez-vous
            </Link>{' '}
            : le compte se cree ensuite.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
